import { useState } from "react";
import { useTranslation } from "react-i18next";

import IntroStep from "./steps/IntroStep";
import SchoolNameStep from "./steps/SchoolNameStep";
import DirectorNameStep from "./steps/DirectorNameStep";
import AdminEmailStep from "./steps/AdminEmailStep";
import AdditionalAdminsStep from "./steps/AdditionalAdminsStep";
import ReviewSubmitStep from "./steps/ReviewSubmitStep";
import SchoolAddressStep from "./steps/SchoolAddressSteps";
import SchoolEmailStep from "./steps/SchoolEmailStep";
import DesiredURLStep from "./steps/DesiredURLStep";
import OptionalContactStep from "./steps/OptionalContactStep";
import OptionalSocialStep from "./steps/OptionalSocialStep";

import '../../bodyComponents/GetStarted/GetStarted.css'

const steps = [
  IntroStep,
  SchoolNameStep,
  DirectorNameStep,
  SchoolAddressStep,
  SchoolEmailStep,
  AdminEmailStep,
  AdditionalAdminsStep,
  DesiredURLStep,
  OptionalContactStep,
  OptionalSocialStep,
  ReviewSubmitStep
];

const stepFieldMap = [
  null,              // 0 Intro
  "schoolName",      // 1
  "directorName",    // 2
  null,              // 3 School Address
  "schoolEmail",     // 4
  "adminEmail",      // 5
  null,              // 6 Additional admins (optional)
  "desiredURL",      // 7
  "website",              // 8 Optional contact
  null,              // 9 Optional social
  null               // 10 Review
];

const isValidWebsite = value => {
  if (!value || value.trim() === "") return true;

  try {
    const url = new URL(
      value.startsWith("http") ? value : `https://${value}`
    );

    const hostname = url.hostname;

    // Must contain at least one dot
    if (!hostname.includes(".")) return false;

    const parts = hostname.split(".");

    // No empty parts (prevents "d.")
    if (parts.some(p => p.length === 0)) return false;

    // TLD must be at least 2 chars
    if (parts[parts.length - 1].length < 2) return false;

    return true;
  } catch {
    return false;
  }
};

export default function GetStartedWizard({ isNavOpen }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

const [formData, setFormData] = useState({
  schoolName: "",
  directorName: "",
  schoolAddress: "",
  schoolEmail: "",
  adminEmail: "",
  additionalAdmins: "",
  desiredURL: "",
  facebookUrl: "",
  instagramUrl: "",
  phoneNumber: "",
  website: "",
  notes: ""
});

// console.log(formData)

const stepRequirements = {
  schoolName: v => v.trim().length > 0,
  directorName: v => v.trim().length > 0,
  schoolEmail: v =>
    v.trim().length === 0 || /\S+@\S+\.\S+/.test(v),
  adminEmail: v =>
    /\S+@\S+\.\S+/.test(v),
  desiredURL: v => v.trim().length > 0,

  website: v =>
    v.trim().length === 0 || isValidWebsite(v)
};





  const totalSteps = steps.length;
  const StepComponent = steps[step];

  const canGoNext = () => {
    const field = stepFieldMap[step];

    // No requirement → always allow
    if (!field) return true;

    const validator = stepRequirements[field];
    return validator ? validator(formData[field] || "") : true;
  };


const handleSubmit = async () => {
  setLoading(true);
  setError("");

  try {
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/get-started`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      }
    );

    if (!res.ok) {
      throw new Error("submit failed");
    }

    setSuccess(true);
  } catch (err) {
    setError(t("Start.errors.submitFailed"));
  } finally {
    setLoading(false);
  }
};


  if (success) {
    return (
      <div className="get-started-wrapper">
        <div className="get-started-card">
          <p className="success-message">
            {t("Start.successMessage")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isNavOpen ? 'body-squeezed' : 'body'}`}>
      <div className="get-started-wrapper">
        <div className="get-started-card">

          <StepComponent
            formData={formData}
            setFormData={setFormData}
            isWebsiteValid={isValidWebsite(formData.website)}
            onSubmit={handleSubmit}
            loading={loading}
            error={error}
          />

          <div className="wizard-footer">
            <span className="step-indicator">
              {t("Start.stepCounter", {
                current: step + 1,
                total: totalSteps
              })}
            </span>

            <div className="wizard-controls">
              {step > 0 && (
                <button onClick={() => setStep(s => s - 1)}>
                  &lt;
                </button>
              )}

              {step < totalSteps - 1 && (
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canGoNext()}
                >
                  &gt;
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
